import { Button } from '@/common/ui/button'
import { useMutateDepositTransaction } from '../../../api/hooks/useMutateDepositTransaction'
import { DepositStatus } from '@/schema'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/common/ui/alert-dialog'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { useState } from 'react'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import ForceCompleteDepositModal from './ForceCompleteDepositModal'
import UpdateDepositStatusModal from './UpdateDepositStatusModal'

interface DepositActionsProps {
	id: string
	status: DepositStatus
	amount: number
}

export default function DepositActions({
	id,
	status,
	amount,
}: DepositActionsProps) {
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_BANK_TRANSACTION,
	)

	const { forceComplete, forceCancel } = useMutateDepositTransaction()
	const [isOpen, setIsOpen] = useState(false)
	const [isCancelOpen, setIsCancelOpen] = useState(false)
	const [cancelNote, setCancelNote] = useState('')

	if (!hasWritePermission) return null

	return (
		<div className="flex items-center gap-2">
			<UpdateDepositStatusModal id={id} currentStatus={status} />
			{![
				DepositStatus.COMPLETED,
				DepositStatus.PAYMENT_GATEWAY_FAILED,
				DepositStatus.MANUAL_COMPLETED,
			].includes(status) && (
				<>
					<ForceCompleteDepositModal
						open={isOpen}
						setOpen={setIsOpen}
						id={id}
						amount={amount}
						forceComplete={forceComplete}
					/>
					{[
						DepositStatus.PENDING,
						DepositStatus.CREATED,
						DepositStatus.PROCESSING,
					].includes(status) && (
						<AlertDialog
							open={isCancelOpen}
							onOpenChange={(open) => {
								setIsCancelOpen(open)
								if (!open) {
									setCancelNote('')
								}
							}}
						>
							<AlertDialogTrigger asChild>
								<Button variant="destructive">ยกเลิก</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>ยืนยันการทำรายการ</AlertDialogTitle>
									<AlertDialogDescription>
										คุณต้องการยกเลิกรายการฝากเงินนี้ใช่หรือไม่?
									</AlertDialogDescription>
									<div className="space-y-2 pt-4">
										<Label
											htmlFor="cancel-deposit-note"
											className="text-sm font-medium"
										>
											หมายเหตุ (ถ้ามี)
										</Label>
										<Textarea
											id="cancel-deposit-note"
											placeholder="ระบุเหตุผลในการยกเลิก..."
											value={cancelNote}
											onChange={(e) => setCancelNote(e.target.value)}
											className="min-h-[80px]"
										/>
									</div>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
									<AlertDialogAction
										type="button"
										onClick={() => {
											forceCancel.mutate({ id, note: cancelNote || undefined })
										}}
									>
										ยืนยัน
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					)}
				</>
			)}
		</div>
	)
}
